/**
 * Imagens: identidade visual da barbearia e fotos de cliente e atendimento.
 *
 * **Como o arquivo trafega.** O navegador reduz a imagem para WebP antes de
 * subir (ver `apps/web/src/lib/image.ts`), então o que chega aqui é pequeno por
 * construção — algumas dezenas de KB. Sendo pequeno, vai como base64 dentro do
 * JSON: o cliente HTTP do app continua sendo um só, sem um caminho especial de
 * `multipart` para manter. O custo é os 33% de inchaço do base64, que sobre
 * 40KB é ruído.
 *
 * O servidor não confia em nada disso. Quem decide o que a imagem é, e se ela
 * entra, é o `checkImage` do core — pelos bytes, não pelo que o app declarou.
 */

import { IMAGE_LIMITS, MAX_PHOTOS_PER_APPOINTMENT } from '@barbearia/core';
import { z } from 'zod';

export { MAX_PHOTOS_PER_APPOINTMENT };

/** Sobra sobre o maior arquivo aceito, já contando o inchaço do base64. */
const MAX_BASE64_LENGTH = Math.ceil((IMAGE_LIMITS.PHOTO.maxBytes * 4) / 3) + 1024;

/**
 * Aceita tanto o base64 puro quanto a data URL que o `canvas.toDataURL`
 * devolve. Obrigar o app a fatiar o prefixo seria uma pegadinha sem ganho.
 */
export const imagePayloadSchema = z
  .string()
  .trim()
  .min(1, 'Envie uma imagem')
  .max(MAX_BASE64_LENGTH, 'Imagem grande demais')
  .transform((value) => value.replace(/^data:image\/[a-z+]+;base64,/, ''))
  .refine((value) => /^[A-Za-z0-9+/]+={0,2}$/.test(value), 'Imagem corrompida no envio');

export const uploadImageSchema = z.object({
  data: imagePayloadSchema,
});

export type UploadImageInput = z.input<typeof uploadImageSchema>;

export const photoMomentSchema = z.enum(['BEFORE', 'AFTER', 'OTHER']);
export type PhotoMoment = z.infer<typeof photoMomentSchema>;

export const PHOTO_MOMENT_LABELS: Record<PhotoMoment, string> = {
  BEFORE: 'Antes',
  AFTER: 'Depois',
  OTHER: 'Registro',
};

export const addAppointmentPhotoSchema = z.object({
  data: imagePayloadSchema,
  moment: photoMomentSchema.default('OTHER'),
  /**
   * O barbeiro pode liberar já na captura. Ausente, a foto nasce fechada: a
   * decisão de mostrar ao cliente é dele, foto a foto, e o padrão silencioso
   * nunca deve ser o mais exposto.
   */
  releasedToClient: z.boolean().default(false),
});

export const updateAppointmentPhotoSchema = z.object({
  releasedToClient: z.boolean(),
});

export interface AppointmentPhotoView {
  id: string;
  url: string;
  moment: PhotoMoment;
  /** Nulo = só a equipe vê. */
  releasedToClientAt: string | null;
  createdAt: string;
  width: number;
  height: number;
}

/** Uma foto na galeria da ficha do cliente, já com o atendimento de origem. */
export interface ClientPhotoView extends AppointmentPhotoView {
  appointmentId: string;
  appointmentStartsAt: string;
  serviceName: string;
}
